// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';

const ACCOMPLISHMENTS_QUERY_KEY = ['accomplishments'] as const;
const CREDITS_QUERY_KEY = ['credits'] as const;

type Suggestion = {
  description: string;
  impact?: string;
  category?: string;
  suggested_tags?: string[];
};

type SuggestAccomplishmentsParams = {
  job_title?: string;
  include_job_descriptions: boolean;
  include_role_highlights: boolean;
};

/** Hook for generating AI accomplishment suggestions. */
export function useSuggestAccomplishments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: SuggestAccomplishmentsParams) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/accomplishments/suggest',
        {
          body: {
            job_title: params.job_title || undefined,
            include_job_descriptions: params.include_job_descriptions,
            include_role_highlights: params.include_role_highlights,
          },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data as Suggestion[];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    },
  });
}

type BulkAccomplishment = {
  description: string;
  impact?: string;
  category?: string;
  tags?: string[];
};

/** Hook for bulk creating accomplishments (typically from accepted suggestions). */
export function useBulkCreateAccomplishments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (accomplishments: BulkAccomplishment[]) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/accomplishments/bulk',
        {
          body: {
            accomplishments: accomplishments.map((accomplishment) => ({
              description: accomplishment.description,
              impact: accomplishment.impact || undefined,
              category: accomplishment.category || undefined,
              tags: accomplishment.tags?.length ? accomplishment.tags : undefined,
            })),
          },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACCOMPLISHMENTS_QUERY_KEY });
    },
  });
}
